// Version-history presentation state (#11819).
//
// "History is open for artifact X" outlives any one pane component: opening
// it from side-by-side presents the artifact in the overlay drawer for as
// long as it is open, and the split→overlay hand-off re-mounts the pane in
// the other host. So the state is page-scoped by design — and since #12187 it
// lives in a zustand store (`artifactHistoryStore.ts`) rather than module
// `let`s, so it has an owner the story harness puts back to its initial state
// before every story. The presentation is an OVERRIDE of the artifact viewer
// mode, never a write to the remembered preference: closing history hands the
// pane back to wherever the user keeps artifacts.
import { useCallback, useEffect } from "react";
import {
  CLOSE_ARTIFACT_OVERLAY_EVENT,
  getArtifactViewerMode,
  overrideArtifactViewerMode,
} from "../FpPages/viewerMode";
import {
  useArtifactHistoryStore,
  type ArtifactSideColumn,
} from "./artifactHistoryStore";
import { documentCloseDetail } from "./documentClose";
import store from "@/redux/store";

/** Whether the store's open-file record has been released to the overlay
 *  drawer and the drawer has yet to show it: the split pane's host is gone
 *  and the drawer's has not mounted. */
const documentHandOffInFlight = (): boolean => {
  const record = store.getState().fileViewerData;
  return Boolean(record.isOpen) && record.doNotOpenDrawer === false;
};

const isColumnOpen = (
  artifactId: string | null | undefined,
  column: ArtifactSideColumn
): boolean => {
  const state = useArtifactHistoryStore.getState();
  return (
    Boolean(artifactId) &&
    state.openFor === artifactId &&
    state.column === column
  );
};

/** Open one of the artifact's side columns, closing the other: version
 *  history and comments share the space beside the content (#13194).
 *  Version history opened from side-by-side presents the artifact in the
 *  overlay while it is open (#11819). Comments open inside the pane in the
 *  presentation it already has, as a document editor's comments do: the
 *  document's margin gives way to the column and the pane never moves, so
 *  nothing re-mounts and the column closes in place. */
const openColumn = (artifactId: string, column: ArtifactSideColumn): void => {
  if (isColumnOpen(artifactId, column)) return;
  useArtifactHistoryStore.setState(
    column === "comments"
      ? { openFor: artifactId, column }
      : { openFor: artifactId, column, activeComment: null, commentDraft: null }
  );
  if (column === "history" && getArtifactViewerMode() === "split") {
    useArtifactHistoryStore.setState({ presentedInOverlay: true });
    overrideArtifactViewerMode("overlay");
  }
};

export const isArtifactHistoryOpen = (
  artifactId: string | null | undefined
): boolean => isColumnOpen(artifactId, "history");

export const openArtifactHistory = (artifactId: string): void =>
  openColumn(artifactId, "history");

export const isArtifactCommentsOpen = (
  artifactId: string | null | undefined
): boolean => isColumnOpen(artifactId, "comments");

export const openArtifactComments = (artifactId: string): void =>
  openColumn(artifactId, "comments");

/** Close whichever side column is open (history or comments), handing the
 *  pane back to the remembered presentation. */
export const closeArtifactHistory = (): void => {
  const {
    openFor,
    presentedInOverlay,
    activeComment,
    commentDraft,
    honouredLink,
  } = useArtifactHistoryStore.getState();
  if (openFor !== null) {
    useArtifactHistoryStore.setState({ openFor: null });
  }
  // Closing the comments column ends the composer and the active thread.
  if (activeComment !== null || commentDraft !== null) {
    useArtifactHistoryStore.setState({
      activeComment: null,
      commentDraft: null,
    });
  }
  // And a `?comment=` link's business: reopened by hand later, the column
  // does not jump to the linked thread.
  if (honouredLink !== null && !honouredLink.activated) {
    useArtifactHistoryStore.setState({
      honouredLink: { ...honouredLink, activated: true },
    });
  }
  if (presentedInOverlay) {
    useArtifactHistoryStore.setState({ presentedInOverlay: false });
    overrideArtifactViewerMode(null);
  }
};

export const toggleArtifactHistory = (artifactId: string): void => {
  if (isArtifactHistoryOpen(artifactId)) closeArtifactHistory();
  else openArtifactHistory(artifactId);
};

export const toggleArtifactComments = (artifactId: string): void => {
  if (isArtifactCommentsOpen(artifactId)) closeArtifactHistory();
  else openArtifactComments(artifactId);
};

/**
 * A pane closing closes its history: the same events that take the panes down
 * (an explicit close, a chat send's transient sweep, the route-change sweep).
 * The last host unmounting (Escape, the drawer's own close) is caught by the
 * hook below.
 *
 * Mounted by Layout, not by the panes: Layout is the component that handles
 * all four of these events and stays mounted across the split→overlay
 * hand-off, during which every pane host can be unmounted while the history
 * state must still be closable.
 */
export const useCloseArtifactHistoryOnPanelEvents = (): void => {
  useEffect(() => {
    const names: string[] = [
      "closeDocumentPanel",
      "closeTransientPanels",
      "closeGlobalFileViewer",
      CLOSE_ARTIFACT_OVERLAY_EVENT,
    ];
    // A document closing beside an artifact is not that artifact's pane
    // closing (#12452): only the viewer that hosts this history closes it.
    const onPanelEvent = (event: Event) => {
      const own = documentCloseDetail(event);
      const { openFor } = useArtifactHistoryStore.getState();
      if (own && own.artifactId !== openFor) return;
      closeArtifactHistory();
    };
    for (const name of names) {
      window.addEventListener(name, onPanelEvent);
    }
    return () => {
      for (const name of names) {
        window.removeEventListener(name, onPanelEvent);
      }
    };
  }, []);
};

/** A pane host counts itself while mounted, so the last host unmounting
 *  closes the artifact's side column (and a hand-off's does not). */
const useSideColumnHost = (artifactId: string | null | undefined): void => {
  useEffect(() => {
    if (!artifactId) return;
    useArtifactHistoryStore.setState((state) => ({ hosts: state.hosts + 1 }));
    return () => {
      useArtifactHistoryStore.setState((state) => ({ hosts: state.hosts - 1 }));
      // After the commit: a hand-off's replacement host has mounted by then;
      // a real close has left nobody. React runs every cleanup and every
      // mount effect of a commit in one synchronous pass, so a microtask
      // queued here runs once that pass is over, which is the whole of what
      // this waits on. It was a 0 ms timer (#12187), a guess at the same
      // moment that a story's clock could hold back indefinitely.
      // A produced file's overlay host mounts a commit later still: the
      // drawer opens from the store's record, released to it (#12481,
      // Gerry: "it puts it in overlay mode, but it doesn't show the version
      // history until I click Version history again"). A record on its way
      // to the drawer means the hand-off is in flight, not that nobody is
      // left.
      queueMicrotask(() => {
        const { hosts, openFor } = useArtifactHistoryStore.getState();
        if (hosts === 0 && openFor === artifactId && !documentHandOffInFlight())
          closeArtifactHistory();
      });
    };
  }, [artifactId]);
};

const useColumnOpen = (
  artifactId: string | null | undefined,
  column: ArtifactSideColumn
): boolean =>
  useArtifactHistoryStore(
    (state): boolean =>
      Boolean(artifactId) &&
      state.openFor === artifactId &&
      state.column === column
  );

/** A pane host's view of the history state for its artifact. */
export const useArtifactHistory = (
  artifactId: string | null | undefined
): { open: boolean; toggle: () => void; close: () => void } => {
  const open = useColumnOpen(artifactId, "history");
  useSideColumnHost(artifactId);
  const toggle = useCallback(() => {
    if (artifactId) toggleArtifactHistory(artifactId);
  }, [artifactId]);
  return { open, toggle, close: closeArtifactHistory };
};

/** A pane host's view of the comments column for its artifact (#13194):
 *  the same presentation as version history, exclusive with it. */
export const useArtifactComments = (
  artifactId: string | null | undefined
): {
  open: boolean;
  toggle: () => void;
  show: () => void;
  close: () => void;
} => {
  const open = useColumnOpen(artifactId, "comments");
  useSideColumnHost(artifactId);
  const toggle = useCallback(() => {
    if (artifactId) toggleArtifactComments(artifactId);
  }, [artifactId]);
  const show = useCallback(() => {
    if (artifactId) openArtifactComments(artifactId);
  }, [artifactId]);
  return { open, toggle, show, close: closeArtifactHistory };
};
